import { asRetriableError } from "./adapters";
import type { CommandResult } from "./commandTypes";

interface RunWithObjectMutexParams<T> {
  locks: Set<string>;
  slug: string;
  commandName: string;
  run: () => Promise<T>;
  onStart?: (slug: string, commandName: string) => void;
  onSuccess?: (slug: string, commandName: string) => void;
  onError?: (slug: string, commandName: string, error: string, retriable: boolean) => void;
}

export const runWithObjectMutex = async <T>({
  locks,
  slug,
  commandName,
  run,
  onStart,
  onSuccess,
  onError,
}: RunWithObjectMutexParams<T>): Promise<CommandResult<T>> => {
  if (locks.has(slug)) {
    return {
      ok: false,
      retriable: true,
      error: `Another command is already running for ${slug}`,
    };
  }

  locks.add(slug);
  onStart?.(slug, commandName);
  try {
    const data = await run();
    onSuccess?.(slug, commandName);
    return { ok: true, data };
  } catch (error) {
    const normalized = asRetriableError(error);
    onError?.(slug, commandName, normalized.message, normalized.retriable);
    return {
      ok: false,
      retriable: normalized.retriable,
      error: normalized.message,
    };
  } finally {
    locks.delete(slug);
  }
};
